<template>
  <div class="container mx-auto p-4">
    <page-header
      title="Outbox Events"
      subtitle="Inspect delivery events and replay dead-lettered records into the main application."
    />

    <div class="mb-4 flex items-center justify-between gap-4">
      <input-search v-model.trim="search" class="max-w-lg grow" placeholder="Search by outbox id, event, user, source event id, error, or product" />

      <filter-select v-model="status" :options="statusOptions" class="mr-auto" />

      <reload-button class="shrink-0" @click="reloadGrid" />
    </div>

    <grid
      ref="gridRef"
      :columns="columns"
      :data-handler="getOutboxEvents"
      row-class="hover:bg-slate-50"
      :per-page="15"
      @error="onError"
      @replay="onReplay"
    />
  </div>
</template>

<script setup>
import { ref, markRaw } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useFetch, watchDebounced } from '@vueuse/core';
import { useRouteQuery } from '@vueuse/router';
import PageHeader from '../components/PageHeader.vue';
import Grid from '../components/grid/Grid.vue';
import InputSearch from '../components/grid/InputSearch.vue';
import ReloadButton from '../components/grid/ReloadButton.vue';
import FilterSelect from '../components/grid/FilterSelect.vue';
import OutboxListActions from '../components/OutboxListActions.vue';
import { apiDomain } from '../composables/useConstants';
import { formatDateTime } from '../utils/formatters';
import { elipsis } from '../utils/elipsis';

const router = useRouter();
const toast = useToast();
const gridRef = ref(null);
const replayingOutboxId = ref(null);

const statusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Sent', value: 'sent' },
  { label: 'Pending', value: 'pending' },
  { label: 'Dead-letter', value: 'dead-letter' }
];

const search = useRouteQuery('search', null);
const status = useRouteQuery('status', 'all');

function reloadGrid(updateQuery = {}) {
  gridRef.value?.reload(updateQuery);
}

watchDebounced(
  search,
  () => reloadGrid({ offset: 0 }),
  { debounce: 500, maxWait: 1000 }
);

watchDebounced(
  status,
  () => reloadGrid({ offset: 0 }),
  { debounce: 100, maxWait: 200 }
);

function getStatusClass(row) {
  const base = 'text-center rounded-lg text-lg scale-70';
  if (row.deadLetteredAt) return `${base} bg-red-100 !text-red-800`;
  if (row.status === 'SENT') return `${base} bg-green-100 !text-green-800`;
  return `${base} bg-amber-100 !text-amber-800`;
}

async function getOutboxEvents({ limit, offset }) {
  const params = new URLSearchParams({
    limit,
    offset,
    status: status.value ?? 'all',
    search: search.value ?? ''
  });
  const { data } = await useFetch(`${apiDomain}/api/stripe/admin/outbox-events?${params}`).get().json();
  return {
    total: data.value?.pagination?.total ?? 0,
    rows: data.value?.data ?? []
  };
}

function onError(message) {
  toast.error(message);
}

async function onReplay(row) {
  replayingOutboxId.value = row.id;
  try {
    await useFetch(`${apiDomain}/api/stripe/admin/outbox-events/${row.id}/replay`).post().json();
    toast.success('Outbox event replayed successfully!');
    reloadGrid({ offset: 0 });
  } catch (error) {
    console.error('Error replaying outbox event:', error);
    toast.error('Failed to replay outbox event.');
  } finally {
    replayingOutboxId.value = null;
  }
}

const columns = [
  {
    title: 'Outbox ID',
    value: row => elipsis(row.id),
    class: 'text-sm font-mono text-xs'
  },
  {
    title: 'Source Event ID',
    value: row => elipsis(row.sourceEventId, 24),
    class: 'text-sm font-medium cursor-pointer hover:underline',
    click: row => router.push(`/stripe-webhook-events?search=${row.sourceEventId}`)
  },
  {
    title: 'User ID',
    value: row => row.userId ? elipsis(row.userId) : '',
    class: 'text-sm text-slate-600'
  },
  {
    title: 'Event Name',
    prop: 'eventName'
  },
  {
    title: 'Product',
    value: row => row.payloadJson?.productKey || row.payloadJson?.product || row.payloadJson?.productType || ''
  },
  {
    title: 'Retries',
    prop: 'retries',
    class: 'text-center font-medium',
    titleClass: '!text-center'
  },
  {
    title: 'Status',
    prop: 'status',
    class: row => getStatusClass(row),
    titleClass: '!text-center'
  },
  {
    title: 'Last Error',
    prop: 'lastError',
    class: 'max-w-[280px] text-sm text-slate-600 truncate'
  },
  {
    title: 'Delivered / Failed At',
    value: row => row.deliveredAt ? formatDateTime(row.deliveredAt) : formatDateTime(row.deadLetteredAt),
    class: 'whitespace-nowrap text-xs'
  },
  {
    title: '',
    class: 'whitespace-nowrap',
    component: markRaw(OutboxListActions),
    props: row => ({
      row,
      isReplaying: replayingOutboxId.value === row.id
    })
  }
];
</script>
